import { AutoId } from 'common/AutoId';
import { DEFAULT_COLOR, hueFromColor } from 'common/ColorUtils';
import { action, makeObservable, observable } from 'mobx';
import { ColorPreset, ColorVariation } from '@sophya/eyekia/lib/schemas/Types';

type ColorVariant = {
  hueTarget: number;
  saturationMultiplier: number;
  valueMultiplier: number;
  key: string;
};

export class ColorPresetStore {
  public baseColor: string;
  baseHue: number;
  // Store the original image's dominant hue (extracted from image analysis)
  public originalImageHue: number = 0;

  public useBaseAsPreset: boolean;

  public presets: ColorVariant[];
  public currentPresetIndex: number = null;

  constructor(
    public updateColor: (rotation: number, saturation: number, value: number) => void,
    private save: () => Promise<void>,
  ) {
    makeObservable(this, {
      baseColor: observable,
      originalImageHue: observable,
      presets: observable,
      currentPresetIndex: observable,
      useBaseAsPreset: observable,
      setBaseColor: action,
      setOriginalImageHue: action,
      rotateHueSlider: action,
      addPreset: action,
      selectPreset: action,
      updateFromSchema: action,
      toggleBasePreset: action,
    });
  }

  public setBaseColor = (color: string) => {
    this.baseColor = color;
    this.baseHue = hueFromColor(color) * 360;
    // Also set this as the original image hue if not set yet
    if (this.originalImageHue === 0) {
      this.originalImageHue = this.baseHue;
    }
    this.updateCanvas();
    this.save();
  };

  // Method to set the original image's hue (called during image analysis)
  public setOriginalImageHue = (hue: number) => {
    this.originalImageHue = hue;
    this.updateCanvas();
  };

  // Internal method for loading from schema - doesn't trigger save
  private setBaseColorInternal = (color: string) => {
    this.baseColor = color;
    this.baseHue = hueFromColor(color) * 360;
    if (this.originalImageHue === 0) {
      this.originalImageHue = this.baseHue;
    }
    this.updateCanvas();
  };

  public rotateHueSlider = (_e, v) => {
    this.presets[this.currentPresetIndex].hueTarget = v;
    this.updateCanvas();
    this.save();
  };

  public rotateSaturationSlider = (_e, v) => {
    this.presets[this.currentPresetIndex].saturationMultiplier = v;
    this.updateCanvas();
    this.save();
  };
  public rotateValueSlider = (_e, v) => {
    this.presets[this.currentPresetIndex].valueMultiplier = v;
    this.updateCanvas();
    this.save();
  };

  // Calculate the rotation needed to go from original image hue to target hue
  private calculateHueRotationForReplacement = (targetHue: number): number => {
    // Convert degrees to -180 to +180 range for proper rotation calculation
    let rotation = targetHue - this.originalImageHue;

    // Normalize to shortest rotation path
    while (rotation > 180) rotation -= 360;
    while (rotation < -180) rotation += 360;

    return rotation;
  };

  updateCanvas = () => {
    if (this.currentPresetIndex === null) {
      // No preset selected - show original colors
      this.updateColor(0, 1.0, 1.0);
    } else {
      const preset = this.presets[this.currentPresetIndex];
      const targetHue = preset.hueTarget;

      // Calculate the rotation needed to achieve absolute hue replacement
      // This rotates from the original image's hue to the target hue
      const absoluteRotation = this.calculateHueRotationForReplacement(targetHue);

      this.updateColor(absoluteRotation, preset.saturationMultiplier, preset.valueMultiplier);
    }
  };
  public toggleBasePreset = () => {
    this.useBaseAsPreset = !this.useBaseAsPreset;
    this.save();
  };
  public addPreset = () => {
    this.presets.push({
      hueTarget: this.baseHue ?? 0,
      saturationMultiplier: 1.0,
      valueMultiplier: 1.0,
      key: AutoId.newId(),
    });
    this.save();
  };
  public removePreset = () => {
    this.presets.splice(this.currentPresetIndex, 1);
    this.currentPresetIndex = null;
    this.updateCanvas();
    this.save();
  };
  public selectPreset = index => {
    this.currentPresetIndex = index;
    this.updateCanvas();
  };

  public updateFromSchema = (preset: ColorPreset) => {
    if (preset) {
      this.setBaseColorInternal(preset.baseColor);

      if (preset.presetColorsDictionary) {
        this.presets = Object.keys(preset.presetColorsDictionary)
          .sort()
          .map(key => {
            const variant = preset.presetColorsDictionary[key];
            return {
              hueTarget: variant.hueRotationDegrees + this.baseHue,
              saturationMultiplier: variant.saturationMultiplier ?? 1.0,
              valueMultiplier: variant.valueMultiplier ?? 1.0,
              key,
            } as ColorVariant;
          });
      } else {
        this.presets = [];
      }
      this.useBaseAsPreset = preset.useBaseAsPreset;
    } else {
      this.baseColor = DEFAULT_COLOR;
      this.baseHue = 0;
      this.presets = [];
      this.useBaseAsPreset = true;
    }
  };

  toSchema = (): ColorPreset => {
    return {
      baseColor: this.baseColor,
      useBaseAsPreset: this.useBaseAsPreset,
      presetColors: this.presets.map(
        v =>
          ({
            hueRotationDegrees: v.hueTarget - this.baseHue,
            saturationMultiplier: v.saturationMultiplier,
            valueMultiplier: v.valueMultiplier,
          } as ColorVariation),
      ),
      presetColorsDictionary: Object.assign(
        {},
        ...this.presets.map(v => ({
          [v.key]: {
            hueRotationDegrees: v.hueTarget - this.baseHue,
            saturationMultiplier: v.saturationMultiplier,
            valueMultiplier: v.valueMultiplier,
          } as ColorVariation,
        })),
      ),
    };
  };
}
